import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { useAuth } from '../context/AuthContext';
import api from '../api/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import {
  ArrowLeft, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, MessageCircle,
  Highlighter, Pencil, Square, Circle, Stamp, MousePointer, Trash2, Save,
  Download, FileText
} from 'lucide-react';

// Self-hosted PDF.js worker as a plain `.js` (served as application/javascript by
// IIS default — `.mjs` 404s without a MIME mapping). Mirrors PDFViewer.js.
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.js`;

const TOOLS = [
  { id: 'select',    icon: MousePointer, label: 'Select' },
  { id: 'comment',   icon: MessageCircle, label: 'Comment' },
  { id: 'highlight', icon: Highlighter,   label: 'Highlight' },
  { id: 'draw',      icon: Pencil,        label: 'Draw' },
  { id: 'rect',      icon: Square,        label: 'Rectangle' },
  { id: 'ellipse',   icon: Circle,        label: 'Ellipse' },
  { id: 'stamp',     icon: Stamp,         label: 'Stamp' }
];

const COLORS = ['#fbbf24', '#ef4444', '#3b82f6', '#10b981', '#a855f7', '#0f172a'];
const STAMPS = [
  { id: 'approved',      label: 'APPROVED',      color: '#10b981' },
  { id: 'rejected',      label: 'REJECTED',      color: '#dc2626' },
  { id: 'reviewed',      label: 'REVIEWED',      color: '#2563eb' },
  { id: 'confidential',  label: 'CONFIDENTIAL',  color: '#7c3aed' },
  { id: 'signature',     label: 'SIGNATURE',     color: '#0f172a' }
];

const DocumentAnnotator = () => {
  const { id: docId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const headers = getAuthHeaders();

  const [doc, setDoc] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [kind, setKind] = useState(null); // 'pdf' | 'image' | 'text' | 'unsupported'
  const [mimeType, setMimeType] = useState('');
  const [textContent, setTextContent] = useState('');
  const [imgNatural, setImgNatural] = useState({ width: 0, height: 0 });
  const [numPages, setNumPages] = useState(null);
  const [page, setPage] = useState(1);
  const [scale, setScale] = useState(1.2);
  const [pageSize, setPageSize] = useState({ width: 0, height: 0 });

  const [tool, setTool] = useState('select');
  const [color, setColor] = useState(COLORS[0]);
  const [stampType, setStampType] = useState(STAMPS[0].id);

  const [annotations, setAnnotations] = useState([]);
  const [drawing, setDrawing] = useState(null); // current in-progress shape
  const [pendingShape, setPendingShape] = useState(null); // awaiting comment text
  const [commentText, setCommentText] = useState('');
  const [selectedId, setSelectedId] = useState(null);

  const overlayRef = useRef(null);
  const contentRef = useRef(null);

  // Load document + detect its kind (pdf / image / text / unsupported), then fetch
  // the file. Annotation is supported for PDF, images and text; Word/Excel and other
  // binary formats show a "download to annotate" message (no stable canvas to draw on).
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const docRes = await api.getDocument(docId, headers);
        if (cancelled) return;
        setDoc(docRes.data);

        let mime = '';
        let fname = (docRes.data?.title || '').toLowerCase();
        let convertedHtml = false;
        try {
          const metaRes = await api.getDocumentPreviewUrl(docId, headers);
          mime = (metaRes.data?.mime_type || '').toLowerCase();
          fname = (metaRes.data?.filename || fname).toLowerCase();
          convertedHtml = metaRes.data?.converted_format === 'html';
        } catch { /* metadata unavailable — detection falls through to 'unsupported' */ }

        // Office/binary formats are never annotatable (Option C). Check these FIRST,
        // because the preview endpoint converts Word/Excel to an HTML preview — we
        // must key off the ORIGINAL document, not the converted preview mime.
        const isOffice = convertedHtml
          || /\.(docx?|xlsx?|pptx?|odt|ods|odp|rtf)$/.test(fname)
          || mime.includes('word') || mime.includes('officedocument')
          || mime.includes('spreadsheet') || mime.includes('ms-excel')
          || mime.includes('presentation');

        let k;
        if (isOffice) k = 'unsupported';
        else if (mime.includes('pdf') || fname.endsWith('.pdf')) k = 'pdf';
        else if (mime.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|tiff?|svg)$/.test(fname)) k = 'image';
        else if (mime.startsWith('text/') || mime.includes('json') || mime.includes('xml') || mime.includes('csv')
                 || /\.(txt|md|csv|json|xml|log|html?)$/.test(fname)) k = 'text';
        else k = 'unsupported';

        if (k === 'unsupported') {
          if (!cancelled) { setMimeType(mime); setKind('unsupported'); }
          return;
        }

        const fileRes = await api.getDocumentPreview(docId, headers);
        if (cancelled) return;
        const ct = fileRes.headers['content-type'] || mime;

        if (k === 'text') {
          const text = await fileRes.data.text();
          if (!cancelled) setTextContent(text);
        } else if (k === 'pdf' || k === 'image') {
          const blob = new Blob([fileRes.data], { type: ct || 'application/pdf' });
          if (!cancelled) setPdfFile(URL.createObjectURL(blob));
        }
        if (!cancelled) { setMimeType(ct); setKind(k); }

        const annRes = await api.listAnnotations(docId, headers);
        if (!cancelled) setAnnotations(annRes.data?.annotations || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Failed to load document');
      }
    };
    load();
    return () => { cancelled = true; };
  }, [docId]);

  const onDocumentLoad = ({ numPages: n }) => setNumPages(n);
  const onPageRenderSuccess = (pageObj) => {
    setPageSize({ width: pageObj.width, height: pageObj.height });
  };

  // For image/text the SVG overlay must match the rendered content's pixel box so
  // normalized [0,1] annotation coordinates map back to the same spot at any zoom.
  const onImageLoad = (e) => {
    const w = e.target.naturalWidth || e.target.width;
    const h = e.target.naturalHeight || e.target.height;
    setImgNatural({ width: w, height: h });
    setNumPages(1);
  };

  const TEXT_BASE_WIDTH = 794; // ~A4 width at 96dpi
  useEffect(() => {
    if (kind === 'image' && imgNatural.width) {
      setPageSize({ width: imgNatural.width * scale, height: imgNatural.height * scale });
    }
  }, [kind, imgNatural, scale]);

  useEffect(() => {
    if (kind === 'text' && contentRef.current) {
      setNumPages(1);
      const measure = () => {
        if (contentRef.current) {
          setPageSize({ width: TEXT_BASE_WIDTH * scale, height: contentRef.current.offsetHeight });
        }
      };
      const raf = requestAnimationFrame(measure);
      return () => cancelAnimationFrame(raf);
    }
  }, [kind, textContent, scale]);

  // Convert mouse coordinates to normalized [0,1] page coords
  const getNormalizedPoint = useCallback((e) => {
    const r = overlayRef.current?.getBoundingClientRect();
    if (!r) return { x: 0, y: 0 };
    return {
      x: Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)),
      y: Math.max(0, Math.min(1, (e.clientY - r.top) / r.height))
    };
  }, []);

  const persistAnnotation = async (geometry, type, opts = {}) => {
    try {
      const payload = {
        type,
        page,
        geometry,
        color,
        text: opts.text || '',
        stamp_type: type === 'stamp' ? stampType : undefined
      };
      const res = await api.createAnnotation(docId, payload, headers);
      setAnnotations((prev) => [...prev, res.data]);
      toast.success(`${type[0].toUpperCase() + type.slice(1)} added`);
    } catch (e) {
      toast.error('Failed to save annotation');
    }
  };

  const onPointerDown = (e) => {
    if (tool === 'select') return;
    const p = getNormalizedPoint(e);
    if (tool === 'comment') {
      setPendingShape({ type: 'comment', geometry: { x: p.x, y: p.y } });
      setCommentText('');
      return;
    }
    if (tool === 'stamp') {
      const stamp = STAMPS.find((s) => s.id === stampType) || STAMPS[0];
      persistAnnotation(
        { x: p.x - 0.08, y: p.y - 0.025, width: 0.16, height: 0.05 },
        'stamp',
        { text: stamp.label }
      );
      return;
    }
    if (tool === 'draw') {
      setDrawing({ type: 'draw', points: [p] });
      return;
    }
    setDrawing({ type: tool, start: p, current: p });
  };

  const onPointerMove = (e) => {
    if (!drawing) return;
    const p = getNormalizedPoint(e);
    if (drawing.type === 'draw') {
      setDrawing({ ...drawing, points: [...drawing.points, p] });
    } else {
      setDrawing({ ...drawing, current: p });
    }
  };

  const onPointerUp = () => {
    if (!drawing) return;
    if (drawing.type === 'draw') {
      if (drawing.points.length > 1) {
        persistAnnotation({ points: drawing.points }, 'draw');
      }
    } else {
      const { start, current, type } = drawing;
      const x = Math.min(start.x, current.x);
      const y = Math.min(start.y, current.y);
      const width = Math.abs(current.x - start.x);
      const height = Math.abs(current.y - start.y);
      if (width > 0.005 && height > 0.005) {
        if (type === 'highlight' || type === 'rect' || type === 'ellipse') {
          // For highlight/rect/ellipse open optional comment input
          setPendingShape({ type, geometry: { x, y, width, height } });
          setCommentText('');
        }
      }
    }
    setDrawing(null);
  };

  const submitPendingShape = async () => {
    if (!pendingShape) return;
    if (pendingShape.type === 'comment' && !commentText.trim()) {
      toast.error('Please enter a comment');
      return;
    }
    await persistAnnotation(pendingShape.geometry, pendingShape.type, { text: commentText.trim() });
    setPendingShape(null);
    setCommentText('');
  };

  const cancelPendingShape = () => {
    setPendingShape(null);
    setCommentText('');
  };

  const deleteAnnotation = async (a) => {
    try {
      await api.deleteAnnotation(docId, a.id, headers);
      setAnnotations((prev) => prev.filter((x) => x.id !== a.id));
      setSelectedId(null);
      toast.success('Annotation deleted');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete');
    }
  };

  const pageAnnotations = annotations.filter((a) => a.page === page);

  const renderShape = (a, isPreview = false) => {
    const stroke = a.color || color;
    const fill = `${stroke}33`;
    const baseProps = {
      onClick: () => !isPreview && setSelectedId(a.id),
      style: { cursor: isPreview ? 'crosshair' : 'pointer' }
    };
    const selectedStroke = selectedId === a.id ? '#0ea5e9' : stroke;
    if (a.type === 'comment') {
      const cx = a.geometry.x * pageSize.width;
      const cy = a.geometry.y * pageSize.height;
      return (
        <g key={a.id || 'preview-c'} {...baseProps}>
          <circle cx={cx} cy={cy} r={10} fill={stroke} stroke="#fff" strokeWidth={2} />
          <text x={cx} y={cy + 4} fontSize="11" textAnchor="middle" fill="#fff" fontWeight="bold">!</text>
        </g>
      );
    }
    if (a.type === 'highlight') {
      return (
        <rect
          key={a.id || 'preview-h'}
          x={a.geometry.x * pageSize.width}
          y={a.geometry.y * pageSize.height}
          width={a.geometry.width * pageSize.width}
          height={a.geometry.height * pageSize.height}
          fill={fill}
          stroke={selectedStroke}
          strokeWidth={selectedId === a.id ? 2 : 0}
          {...baseProps}
        />
      );
    }
    if (a.type === 'rect') {
      return (
        <rect
          key={a.id || 'preview-r'}
          x={a.geometry.x * pageSize.width}
          y={a.geometry.y * pageSize.height}
          width={a.geometry.width * pageSize.width}
          height={a.geometry.height * pageSize.height}
          fill="none"
          stroke={selectedStroke}
          strokeWidth={selectedId === a.id ? 3 : 2}
          {...baseProps}
        />
      );
    }
    if (a.type === 'ellipse') {
      return (
        <ellipse
          key={a.id || 'preview-e'}
          cx={(a.geometry.x + a.geometry.width / 2) * pageSize.width}
          cy={(a.geometry.y + a.geometry.height / 2) * pageSize.height}
          rx={(a.geometry.width / 2) * pageSize.width}
          ry={(a.geometry.height / 2) * pageSize.height}
          fill="none"
          stroke={selectedStroke}
          strokeWidth={selectedId === a.id ? 3 : 2}
          {...baseProps}
        />
      );
    }
    if (a.type === 'draw') {
      const pts = (a.geometry.points || []).map((p) => `${p.x * pageSize.width},${p.y * pageSize.height}`).join(' ');
      return (
        <polyline
          key={a.id || 'preview-d'}
          points={pts}
          fill="none"
          stroke={selectedStroke}
          strokeWidth={selectedId === a.id ? 3 : 2}
          strokeLinecap="round"
          strokeLinejoin="round"
          {...baseProps}
        />
      );
    }
    if (a.type === 'stamp') {
      const x = a.geometry.x * pageSize.width;
      const y = a.geometry.y * pageSize.height;
      const w = a.geometry.width * pageSize.width;
      const h = a.geometry.height * pageSize.height;
      const stampLabel = (a.text || a.stamp_type || 'STAMP').toUpperCase();
      const stampColor = STAMPS.find((s) => s.id === a.stamp_type)?.color || stroke;
      return (
        <g key={a.id || 'preview-s'} {...baseProps} transform={`rotate(-12 ${x + w / 2} ${y + h / 2})`}>
          <rect x={x} y={y} width={w} height={h} fill="none" stroke={stampColor} strokeWidth={3} rx={4} />
          <text x={x + w / 2} y={y + h / 2 + 4} textAnchor="middle" fontSize={Math.max(10, h * 0.5)} fontWeight="bold" fill={stampColor}>
            {stampLabel}
          </text>
        </g>
      );
    }
    return null;
  };

  const renderInProgress = () => {
    if (!drawing) return null;
    if (drawing.type === 'draw') {
      const pts = drawing.points.map((p) => `${p.x * pageSize.width},${p.y * pageSize.height}`).join(' ');
      return <polyline points={pts} fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />;
    }
    const { start, current, type } = drawing;
    const x = Math.min(start.x, current.x) * pageSize.width;
    const y = Math.min(start.y, current.y) * pageSize.height;
    const w = Math.abs(current.x - start.x) * pageSize.width;
    const h = Math.abs(current.y - start.y) * pageSize.height;
    if (type === 'highlight') return <rect x={x} y={y} width={w} height={h} fill={`${color}33`} />;
    if (type === 'rect') return <rect x={x} y={y} width={w} height={h} fill="none" stroke={color} strokeWidth={2} strokeDasharray="4 2" />;
    if (type === 'ellipse') return <ellipse cx={x + w / 2} cy={y + h / 2} rx={w / 2} ry={h / 2} fill="none" stroke={color} strokeWidth={2} strokeDasharray="4 2" />;
    return null;
  };

  return (
    <div data-testid="document-annotator" className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/documents/${docId}`)} data-testid="annotator-back">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to document
          </Button>
          <h1 className="text-xl font-semibold text-slate-900 truncate">
            {doc?.title || 'Annotate document'}
          </h1>
        </div>
      </div>

      {/* Toolbar */}
      <Card>
        <CardContent className="p-3 flex flex-wrap items-center gap-3">
          <div className="flex gap-1" role="toolbar" aria-label="annotation tools">
            {TOOLS.map(({ id, icon: Icon, label }) => (
              <Button
                key={id}
                size="sm"
                variant={tool === id ? 'default' : 'outline'}
                onClick={() => { setTool(id); setSelectedId(null); }}
                title={label}
                data-testid={`tool-${id}`}
              >
                <Icon className="w-4 h-4" />
              </Button>
            ))}
          </div>

          <div className="h-6 w-px bg-slate-200" />

          <div className="flex items-center gap-1.5">
            <span className="text-xs text-slate-500 mr-1">Color</span>
            {COLORS.map((c) => (
              <button
                key={c}
                onClick={() => setColor(c)}
                title={c}
                data-testid={`color-${c.replace('#', '')}`}
                className={`w-5 h-5 rounded-full border-2 transition-transform ${color === c ? 'border-slate-900 scale-110' : 'border-white shadow'}`}
                style={{ backgroundColor: c }}
              />
            ))}
          </div>

          {tool === 'stamp' && (
            <>
              <div className="h-6 w-px bg-slate-200" />
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-slate-500">Stamp</span>
                <select
                  value={stampType}
                  onChange={(e) => setStampType(e.target.value)}
                  data-testid="stamp-select"
                  className="text-xs border border-slate-200 rounded-md px-2 py-1 bg-white"
                >
                  {STAMPS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
              </div>
            </>
          )}

          <div className="ml-auto flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setScale((s) => Math.max(0.5, s - 0.2))} data-testid="annotator-zoom-out"><ZoomOut className="w-4 h-4" /></Button>
            <span className="text-xs text-slate-500 w-12 text-center">{Math.round(scale * 100)}%</span>
            <Button variant="outline" size="sm" onClick={() => setScale((s) => Math.min(3, s + 0.2))} data-testid="annotator-zoom-in"><ZoomIn className="w-4 h-4" /></Button>
            <div className="h-6 w-px bg-slate-200" />
            <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} data-testid="annotator-prev-page"><ChevronLeft className="w-4 h-4" /></Button>
            <span className="text-xs text-slate-500 w-16 text-center">{page} / {numPages || '—'}</span>
            <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(numPages || p, p + 1))} disabled={page >= (numPages || 1)} data-testid="annotator-next-page"><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Document + overlay */}
        <div className="lg:col-span-3 bg-slate-100 rounded-lg overflow-auto p-4 flex justify-center">
          {kind === 'unsupported' ? (
            <div className="p-12 text-center max-w-md" data-testid="annotator-unsupported">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-700 font-medium mb-1">Annotation isn't available for this file type</p>
              <p className="text-sm text-slate-500 mb-5">
                In-document annotation is supported for PDF, image and text files.
                Word, Excel and other formats can't be annotated directly — download the
                file to review or annotate it in its native application.
              </p>
              <Button onClick={() => navigate(`/documents/${docId}`)} data-testid="annotator-download-hint">
                <Download className="w-4 h-4 mr-1.5" /> Back to document to download
              </Button>
            </div>
          ) : !kind ? (
            <div className="p-12 text-slate-400 text-center">Loading document…</div>
          ) : (
            <div className="relative inline-block shadow-lg bg-white" data-testid="annotator-canvas">
              {kind === 'pdf' && pdfFile && (
                <Document file={pdfFile} onLoadSuccess={onDocumentLoad} loading={<div className="p-8 text-slate-500">Loading PDF…</div>}>
                  <Page
                    pageNumber={page}
                    scale={scale}
                    onRenderSuccess={onPageRenderSuccess}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                  />
                </Document>
              )}
              {kind === 'image' && pdfFile && (
                <img
                  src={pdfFile}
                  alt={doc?.title || 'Document'}
                  onLoad={onImageLoad}
                  style={{ width: imgNatural.width ? imgNatural.width * scale : 'auto', height: 'auto', display: 'block' }}
                  data-testid="annotator-image"
                />
              )}
              {kind === 'text' && (
                <div
                  ref={contentRef}
                  className="bg-white text-slate-900 whitespace-pre-wrap break-words font-mono leading-relaxed"
                  style={{ width: TEXT_BASE_WIDTH * scale, padding: 32 * scale, fontSize: 13 * scale }}
                  data-testid="annotator-text"
                >
                  {textContent || ' '}
                </div>
              )}
              {pageSize.width > 0 && (
                <svg
                  ref={overlayRef}
                  width={pageSize.width}
                  height={pageSize.height}
                  className="absolute top-0 left-0"
                  style={{ cursor: tool === 'select' ? 'default' : 'crosshair', pointerEvents: 'all' }}
                  onMouseDown={onPointerDown}
                  onMouseMove={onPointerMove}
                  onMouseUp={onPointerUp}
                  onMouseLeave={onPointerUp}
                >
                  {pageAnnotations.map((a) => renderShape(a))}
                  {renderInProgress()}
                </svg>
              )}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-3">
          <Card>
            <CardContent className="p-3">
              <p className="text-xs font-semibold text-slate-700 mb-2">Annotations on page {page}</p>
              {pageAnnotations.length === 0 ? (
                <p className="text-xs text-slate-400 py-3 text-center" data-testid="annotation-list">No annotations yet</p>
              ) : (
                <div className="space-y-2 max-h-[480px] overflow-y-auto" data-testid="annotation-list">
                  {pageAnnotations.map((a) => (
                    <div
                      key={a.id}
                      onClick={() => setSelectedId(a.id)}
                      className={`p-2 rounded border cursor-pointer ${selectedId === a.id ? 'border-blue-400 bg-blue-50' : 'border-slate-200 hover:bg-slate-50'}`}
                      data-testid={`annotation-item-${a.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold text-slate-700 capitalize">{a.type}</span>
                        <button
                          className="text-slate-400 hover:text-red-500"
                          onClick={(e) => { e.stopPropagation(); deleteAnnotation(a); }}
                          data-testid={`annotation-delete-${a.id}`}
                          title="Delete"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      {a.text && <p className="text-xs text-slate-600 mt-1 break-words">{a.text}</p>}
                      <p className="text-[10px] text-slate-400 mt-1">{a.created_by_name}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Comment dialog */}
      <Dialog open={!!pendingShape} onOpenChange={(o) => !o && cancelPendingShape()}>
        <DialogContent data-testid="annotation-comment-dialog">
          <DialogHeader>
            <DialogTitle>
              {pendingShape?.type === 'comment' ? 'Add comment' : `Optional note for ${pendingShape?.type}`}
            </DialogTitle>
          </DialogHeader>
          <Textarea
            data-testid="annotation-comment-input"
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            placeholder={pendingShape?.type === 'comment' ? 'Type your comment…' : 'Optional note (leave blank to skip)'}
            rows={4}
          />
          <DialogFooter>
            <Button variant="outline" onClick={cancelPendingShape} data-testid="annotation-comment-cancel">Cancel</Button>
            <Button onClick={submitPendingShape} data-testid="annotation-comment-save">
              <Save className="w-4 h-4 mr-1.5" /> Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DocumentAnnotator;
